import * as api from '../../api';
import * as packActions from './actions';
import { message } from '@myua/kit';
import { getAssetsFilter, getPermissions } from '../app/selectors';
import { downloadDOM } from '../../../general/utils/download';
import { Asset, urlPackDownload } from '../../api';
import { EMPTY_TOTAL, LoadingMode, newScrollHash } from '../finder/model';
import { loadWithPagination } from '../finder/listeners';
import {
  getAllInProgress,
  getData,
  getHasNextPage,
  getInProgress,
  getRelatedSharing,
  getScrollHash,
} from './selectors';
import { getSortingFieldKey, getSortingOrder } from '../sorting/selectors';
import { takeLatest, takeEvery, takeOnce } from '../../listenerMiddleware';
import * as appActions from '../app/actions';
import { Permission } from '../../../general/model';
import { showSuccessNotification } from '../sharing/components/SuccessNotification';

export const packListeners = () => {
  init();
  watchLoadPackRequested();
  watchAssetToggled();
  watchClearPackClicked();
  watchDownloadPackClicked();
  watchSaveRelatedSharingClicked();
};

function init() {
  takeOnce(appActions.initialized, (action, { getState, dispatch }) => {
    // Надо загрузить пак, не дожидаясь пока юзер перейдет в режим просмотра пака, т.к. информация
    // о паке нужна и в обычном режиме, чтобы отображать счетчик и иконки добавления/удаления ассета.
    const permissions = getPermissions(getState());
    if (permissions[Permission['fs.assetlab_packs']]) {
      dispatch(packActions.loadPackRequested({ mode: LoadingMode.refresh }));
    }
  });
}

function watchLoadPackRequested() {
  takeLatest(packActions.loadPackRequested, async ({ payload }, listenerApi) => {
    const { getState } = listenerApi;
    const { mode } = payload;

    const errorState: api.LoadPackReturn = { assets: [], total: EMPTY_TOTAL, asset_ids: [] };
    const sortingOrder = getSortingOrder(getState());
    const sortingFieldKey = getSortingFieldKey(getState());

    await loadWithPagination(
      {
        mode,
        getData,
        getScrollHash,
        setLoading: packActions.setLoading,
        assetsChanged: packActions.assetsChanged,
        errorMessage: 'Pack has not been loaded',
        errorState,
        onRequest: (offset) =>
          api.loadPack({ orderBy: sortingFieldKey, ordering: sortingOrder, offset }),
      },
      listenerApi,
    );
  });
}

function watchAssetToggled() {
  takeEvery(packActions.assetsToggled, async ({ payload }, { getState, dispatch }) => {
    const { assets, add, selectAll } = payload;

    // Если какой-то из затронутых ассетов уже в обработке, то выходим из саги.
    // UI, в принципе, не дает возникнуть такой ситуации.
    const allInProgress = getAllInProgress(getState());
    const inProgress = getInProgress(getState());
    if (allInProgress || assets?.some(({ id }) => inProgress[id])) {
      return;
    }

    const ids = (assets ?? []).map(({ id }) => id);

    let result;

    if (selectAll) {
      dispatch(packActions.setAllInProgress({ allInProgress: true }));
      const filter = getAssetsFilter(getState());
      if (add) {
        result = await api.addToPack({ filter });
      } else {
        result = await api.deleteFromPack({ filter });
      }
      dispatch(packActions.setAllInProgress({ allInProgress: false }));
    } else {
      dispatch(packActions.setInProgress({ ids, inProgress: true }));
      if (add) {
        result = await api.addToPack({ ids });
      } else {
        result = await api.deleteFromPack({ ids });
      }
      dispatch(packActions.setInProgress({ ids, inProgress: false }));
    }

    if (!result.success) {
      return;
    }

    const assetsInPack: Partial<Record<Asset['id'], boolean>> = {};
    result.data.response.asset_ids.forEach((id) => {
      assetsInPack[id] = true;
    });
    const data = getData(getState());
    const newAssets = add ? data.assets : data.assets.filter(({ id }) => assetsInPack[id]);
    const hasNextPage = getHasNextPage(getState());
    const scrollHash = getScrollHash(getState());
    dispatch(
      packActions.assetsChanged({
        data: {
          assets: newAssets,
          total: result.data.response.total,
          asset_ids: result.data.response.asset_ids,
        },
        hasNextPage,
        scrollHash,
      }),
    );
  });
}

function watchClearPackClicked() {
  takeLatest(packActions.clearPackClicked, async (action, { dispatch, signal }) => {
    const messageKey = 'pack.delete';
    void message.loading({ key: messageKey, content: 'Deleting...' });
    const { success } = await api.clearPack();
    if (signal.aborted) {
      return;
    }
    if (success) {
      dispatch(
        packActions.assetsChanged({
          data: { assets: [], total: EMPTY_TOTAL, asset_ids: [] },
          hasNextPage: false,
          scrollHash: newScrollHash(),
        }),
      );
      void message.success({ key: messageKey, content: 'Pack has been cleared' });
    } else {
      void message.error({ key: messageKey, content: 'Pack has not been cleared' });
    }
  });
}

function watchDownloadPackClicked() {
  takeEvery(packActions.downloadPackClicked, () => {
    downloadDOM(urlPackDownload());
  });
}

function watchSaveRelatedSharingClicked() {
  takeEvery(packActions.saveRelatedSharingClicked, async (action, { getState, dispatch }) => {
    const relatedSharing = getRelatedSharing(getState());
    if (!relatedSharing) {
      return;
    }

    dispatch(packActions.setRelatedSharingInProgress({ inProgress: true }));
    const result = await api.updateSharingAssets({ sharingId: relatedSharing.id });
    dispatch(packActions.setRelatedSharingInProgress({ inProgress: false }));

    if (result.success) {
      void showSuccessNotification(relatedSharing.id, (copied) =>
        copied ? 'Shared link has been updated and copied' : 'Shared link has been updated',
      );
      dispatch(packActions.setRelatedSharing({ relatedSharing: undefined }));
    } else {
      void message.error('Failed to update sharing');
    }
  });
}
